"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CustomizationSwatch } from "@/components/commerce/customization-swatches";

export type CartItem = {
  selectionId: string;
  productId: string;
  customizationId: string | null;
  productName: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  customizationDescription: string | null;
  customizationSwatches: CustomizationSwatch[];
};

export type Cart = {
  cartId: string;
  guestToken: string | null;
  items: CartItem[];
  subtotal: number;
};

type CartContextValue = {
  cart: Cart | null;
  loading: boolean;
  error: string | null;
  count: number;
  isOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  addItem: (productId: string, customizationId?: string | null, quantity?: number, options?: AddItemOptions) => Promise<void>;
  updateItem: (selectionId: string, quantity: number) => Promise<void>;
  removeItem: (selectionId: string) => Promise<void>;
  clearCart: () => Promise<void>;
  refresh: () => Promise<void>;
};

type AddItemOptions = {
  // Set false by a caller that's about to navigate straight to /checkout (Buy Now) -- opening the
  // drawer right before an immediate route change is just a pointless flash, worst on mobile where
  // the drawer is a large fixed-position panel. Defaults true, so every existing "Add to Cart" caller
  // keeps its current confirm-by-opening-the-drawer behavior unchanged.
  openCartOnAdd?: boolean;
};

const CartContext = createContext<CartContextValue | null>(null);

async function parseCartResponse(response: Response): Promise<Cart> {
  const data = await response.json();
  if (!response.ok) throw new Error(data?.errors?.[0] ?? "Cart request failed");
  return data as Cart;
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [cart, setCart] = useState<Cart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const response = await fetch("/api/cart");
      setCart(await parseCartResponse(response));
      setError(null);
    } catch {
      setError("Couldn't load your cart. Try refreshing the page.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Not calling `refresh` here on purpose -- lifting the mount-time fetch into its own effect-local
  // closure (rather than an effect depending on the memoized `refresh` callback) is what satisfies
  // react-hooks/set-state-in-effect; `refresh` itself stays available for on-demand re-fetching.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/cart");
        const data = await parseCartResponse(response);
        if (!cancelled) {
          setCart(data);
          setError(null);
        }
      } catch {
        if (!cancelled) setError("Couldn't load your cart. Try refreshing the page.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const addItem = useCallback(async (productId: string, customizationId: string | null = null, quantity = 1, options?: AddItemOptions) => {
    setError(null);
    try {
      const response = await fetch("/api/cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ productId, customizationId, quantity }),
      });
      setCart(await parseCartResponse(response));
      if (options?.openCartOnAdd !== false) setIsOpen(true);
    } catch {
      setError("Couldn't add that to your cart. Please try again.");
    }
  }, []);

  const updateItem = useCallback(async (selectionId: string, quantity: number) => {
    setError(null);
    try {
      const response = await fetch(`/api/cart/items/${selectionId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quantity }),
      });
      setCart(await parseCartResponse(response));
    } catch {
      setError("Couldn't update that item. Please try again.");
    }
  }, []);

  const removeItem = useCallback(async (selectionId: string) => {
    setError(null);
    try {
      const response = await fetch(`/api/cart/items/${selectionId}`, { method: "DELETE" });
      setCart(await parseCartResponse(response));
    } catch {
      setError("Couldn't remove that item. Please try again.");
    }
  }, []);

  // Called once a payment is actually confirmed (see OrderConfirmationStatus), not at checkout time --
  // see CheckoutService.CheckoutAsync's doc comment for why cart-clearing moved out of order creation.
  const clearCart = useCallback(async () => {
    setError(null);
    try {
      const response = await fetch("/api/cart", { method: "DELETE" });
      setCart(await parseCartResponse(response));
    } catch {
      // Best-effort -- the order is already confirmed regardless, so a failure here just means a stale
      // cart badge until the next natural refresh(), not a lost purchase.
    }
  }, []);

  const count = useMemo(() => cart?.items.reduce((sum, item) => sum + item.quantity, 0) ?? 0, [cart]);

  const value = useMemo<CartContextValue>(
    () => ({
      cart,
      loading,
      error,
      count,
      isOpen,
      openCart: () => setIsOpen(true),
      closeCart: () => setIsOpen(false),
      toggleCart: () => setIsOpen((open) => !open),
      addItem,
      updateItem,
      removeItem,
      clearCart,
      refresh,
    }),
    [cart, loading, error, count, isOpen, addItem, updateItem, removeItem, clearCart, refresh]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
}
